import styled from "styled-components"
import BodyText from "components/Typography/BodyText"
import clsx from "clsx"
import Spinner from "../spinner/Spinner"

const IconWrapper = styled.span`
  font-size: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`
const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`
const StyledButton = styled.button`
  text-align: center;
  color: ${(props) => props.theme.palette.white};
  padding: 0 48px;
  height: 45px;

  background: ${(props) => props.theme.palette.subtitle};
  border-radius: 40px;
  overflow: hidden;
  cursor: pointer;
  transition: 0.2s;
  &:not(.disabled) {
    @property --myColor1 {
      syntax: "<color>";
      initial-value: ${(props) => props.theme.palette.primary_alt_dark};
      inherits: false;
    }
    @property --myColor2 {
      syntax: "<color>";
      initial-value: ${(props) => props.theme.palette.primary_one};
      inherits: false;
    }
    transition:
      --myColor1 0.3s,
      --myColor2 0.3s;
    background: linear-gradient(180deg, var(--myColor1), var(--myColor2));
    &:hover {
      --myColor1: ${(props) => props.theme.palette.secondary_four};
      --myColor2: ${(props) => props.theme.palette.secondary_four};
    }
  }
  &.disabled {
    background: ${(props) => props.theme.palette.disabled};
    color: ${(props) => props.theme.palette.white};
    cursor: not-allowed;
  }
  &.fill-width {
    width: 100%;
  }
  &.primary {
    background: ${(props) => props.theme.palette.primary_one};
  }
  &.informative {
    background: ${(props) => props.theme.palette.informative};
  }
  &.freeTrial {
    background: ${(props) => props.theme.palette.secondary_four};
  }
  &.premium {
    background: ${(props) => props.theme.palette.white};
    color: ${(props) => props.theme.palette.primary_one};
  }
  &.button-api-key-copy {
    background: linear-gradient(
      180deg,
      ${(props) => props.theme.palette.primary_alt_dark} 0%,
      ${(props) => props.theme.palette.primary_one} 100%
    );
    &:hover {
      background: linear-gradient(
        180deg,
        ${(props) => props.theme.palette.primary_alt_dark} 0%,
        ${(props) => props.theme.palette.primary_one} 100%
      );
    }
  }
  display: block;
  &.secondary {
    background:
      linear-gradient(white, white) padding-box,
      linear-gradient(
          to top,
          ${(props) => props.theme.palette.primary_one},
          ${(props) => props.theme.palette.primary_one}
        )
        border-box;
    border-radius: 40px;
    border: 1px solid transparent;
    color: ${(props) => props.theme.palette.primary_one};
  }
  &.secondary-medium {
    background:
      linear-gradient(white, white) padding-box,
      linear-gradient(
          to top,
          ${(props) => props.theme.palette.secondary_medium},
          ${(props) => props.theme.palette.secondary_medium}
        )
        border-box;
    border-radius: 40px;
    border: 1px solid transparent;
    color: ${(props) => props.theme.palette.secondary_medium};
  }
  &.secondary-error {
    background: ${(props) => props.theme.palette.white};
    border-radius: 40px;
    border: 1px solid ${(props) => props.theme.palette.error};
    color: ${(props) => props.theme.palette.error};
    &:hover {
      background: ${(props) => props.theme.palette.white};
      box-shadow: 0 0 0 1px ${(props) => props.theme.palette.error};
    }
    &.disabled {
      border: 1px solid ${(props) => props.theme.palette.disabled};
      color: ${(props) => props.theme.palette.disabled};
      &:hover {
        box-shadow: unset;
      }
    }
  }
  &.secondary-attention {
    background: ${(props) => props.theme.palette.white};
    border: 1px solid ${(props) => props.theme.palette.informative};
    color: ${(props) => props.theme.palette.informative};

    &.disabled {
      border: 1px solid ${(props) => props.theme.palette.disabled};
      color: ${(props) => props.theme.palette.disabled};

      &:hover {
        box-shadow: unset;
      }
    }
  }
  &.no-border {
    border: none;
    background: none;
    color: ${(props) => props.theme.palette.primary_one};
    &.disabled {
      color: ${(props) => props.theme.palette.disabled};
    }
  }
  &.attention-error {
    background: ${(props) => props.theme.palette.error};
    color: ${(props) => props.theme.palette.white};
    &:hover {
      background: ${(props) => props.theme.palette.error};
      opacity: 0.6;
    }
  }
  &.attention-success {
    background: ${(props) => props.theme.palette.success};
    color: ${(props) => props.theme.palette.white};
  }
  &.attention-info {
    background: ${(props) => props.theme.palette.primary_one};
    color: ${(props) => props.theme.palette.white};
  }
  &.attention-attention {
    background: ${(props) => props.theme.palette.informative};
    color: ${(props) => props.theme.palette.white};
  }
  &.height-small {
    height: 30px;
  }
  &.shadow {
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  }
  &.optinal-hover-secondary {
    &:hover {
      background: linear-gradient(
        180deg,
        ${(props) => props.theme.palette.primary_alt_dark} 0%,
        ${(props) => props.theme.palette.primary_one} 100%
      );
      color: ${(props) => props.theme.palette.white};
    }
    &.disabled {
      background: ${(props) => props.theme.palette.disabled};
      color: ${(props) => props.theme.palette.white};
      cursor: not-allowed;
    }
  }
  &.background-gradient {
    background: var(--Gradient-Front, linear-gradient(251deg, #6729ea 29.56%, #002c98 77.48%));
    &.disabled {
      background: ${(props) => props.theme.palette.disabled};
      color: ${(props) => props.theme.palette.white};
      cursor: not-allowed;
    }
  }

  &.isSmall {
    height: 40px;
  }

  &.no-padding {
    padding: 0;
  }
`

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean
  type?: "button" | "submit" | "reset"
  children?: React.ReactNode
  label?: string
  className?: string
  testId?: string
  loading?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  isSmall?: boolean
}
const Button = (props: ButtonProps) => {
  const {
    disabled = false,
    type,
    label,
    className,
    loading = false,
    children,
    testId,
    iconLeft,
    iconRight,
    isSmall = false,
    ...rest
  } = props

  const bodyTextSize = isSmall ? "small" : "medium"

  return (
    <StyledButton
      data-testid={testId}
      disabled={disabled}
      type={type}
      className={clsx(className, { disabled, isSmall })}
      {...rest}
    >
      {loading && <Spinner className={className} />}
      {!loading && (
        <ButtonContent>
          {iconLeft && <IconWrapper>{iconLeft}</IconWrapper>}
          {children ?? (
            <BodyText weight="semi-bold" size={bodyTextSize}>
              {label}
            </BodyText>
          )}
          {iconRight && <IconWrapper>{iconRight}</IconWrapper>}
        </ButtonContent>
      )}
    </StyledButton>
  )
}

export default Button
